/*
 * SHWRA – scroll-driven 3D product presentation (Three.js / WebGL).
 *
 * HONESTY NOTE: there is no scanned or manufacturer 3D model of this product yet.
 * The model below is built procedurally from the proportions and details that are
 * visible in the supplier photos (rounded body, clear front window with the inner
 * holder pads, round speaker grille, button and indicator). It is an approximation,
 * not a scan. The rear is never shown because no reference for it exists.
 * When a real GLB is available, replace buildModel() with a GLTFLoader call and keep
 * the rest (scroll mapping, camera, captions, fallbacks) as is.
 *
 * Scroll position drives the animation; nothing autoplays and scrolling is never hijacked.
 */
const SEL = '[data-sh-3d]';

function boot() { document.querySelectorAll(SEL).forEach(init); }
if (document.readyState !== 'loading') boot();
else document.addEventListener('DOMContentLoaded', boot);
document.addEventListener('shopify:section:load', function (e) {
  var el = e.target && e.target.querySelector(SEL);
  if (el) init(el);
});

function webglOK() {
  try {
    var c = document.createElement('canvas');
    return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
  } catch (e) { return false; }
}

function init(root) {
  if (root.__sh3d) return;
  root.__sh3d = true;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!webglOK()) { root.classList.add('sh-3d--static', 'sh-3d--no3d'); return; }
  if (reduce) root.classList.add('sh-3d--static');
  // Only download Three.js when the section is getting close to the viewport.
  var io = new IntersectionObserver(function (entries) {
    if (!entries.some(function (e) { return e.isIntersecting; })) return;
    io.disconnect();
    start(root, reduce).catch(function (err) {
      console.warn('[SHWRA 3D] falling back to image:', err);
      root.classList.add('sh-3d--static', 'sh-3d--no3d');
    });
  }, { rootMargin: '150% 0px' });
  io.observe(root);
}

/* ---------- helpers ---------- */
var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };
var easeIO = function (t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; };

function roundedRectPath(P, x, y, w, h, r) {
  var p = new P();
  p.moveTo(x + r, y);
  p.lineTo(x + w - r, y);
  p.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
  p.lineTo(x + w, y + h - r);
  p.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
  p.lineTo(x + r, y + h);
  p.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
  p.lineTo(x, y + r);
  p.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
  return p;
}

function canvasTex(T, renderer, w, h, draw) {
  var c = document.createElement('canvas');
  c.width = w; c.height = h;
  draw(c.getContext('2d'), w, h);
  var t = new T.CanvasTexture(c);
  t.colorSpace = T.SRGBColorSpace;
  t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  return t;
}

/* Soft studio used only to light/reflect the product (image-based lighting). */
function studio(T) {
  var s = new T.Scene();
  s.add(new T.Mesh(new T.BoxGeometry(14, 9, 14), new T.MeshBasicMaterial({ color: 0x6f7275, side: T.BackSide })));
  function panel(w, h, k, x, y, z) {
    var m = new T.Mesh(new T.PlaneGeometry(w, h), new T.MeshBasicMaterial({ color: new T.Color(k, k, k), side: T.DoubleSide }));
    m.position.set(x, y, z); m.lookAt(0, 0, 0); s.add(m);
  }
  panel(7, 2.4, 5, 0, 4.4, 0.5);    // overhead softbox
  panel(2.2, 5, 3.6, -6.5, 1, 2.5); // key, left
  panel(1.6, 5, 2.6, 6.5, 0.5, -1); // rim, right
  panel(5, 1.6, 1.6, 0, 0.4, 6.8);  // front fill
  return s;
}

/* ---------- the product model (approximation from photos) ---------- */
function buildModel(T, renderer) {
  var W = 2.6, H = 1.12, R = 0.2, DEPTH = 0.28, BEV = 0.045;
  var win = { w: 1.66, h: 0.86, cx: -0.38, cy: 0, r: 0.08 };
  var panelX = (W / 2 + (win.cx + win.w / 2)) / 2;

  var bodyMat = new T.MeshPhysicalMaterial({ color: 0xefefec, roughness: 0.34, metalness: 0, clearcoat: 0.7, clearcoatRoughness: 0.22 });
  var accentMat = new T.MeshPhysicalMaterial({ color: 0xdfe2e4, roughness: 0.4, clearcoat: 0.5, clearcoatRoughness: 0.3 });

  // Body: rounded slab with the phone window cut through it.
  var shape = roundedRectPath(T.Shape, -W / 2, -H / 2, W, H, R);
  shape.holes.push(roundedRectPath(T.Path, win.cx - win.w / 2, win.cy - win.h / 2, win.w, win.h, win.r));
  var bodyGeo = new T.ExtrudeGeometry(shape, { depth: DEPTH, bevelEnabled: true, bevelThickness: BEV, bevelSize: BEV * 0.9, bevelSegments: 5, curveSegments: 24 });
  bodyGeo.translate(0, 0, -DEPTH / 2);
  var body = new T.Mesh(bodyGeo, bodyMat);
  var front = DEPTH / 2 + BEV;

  // Inside of the holder, as seen through the clear front in the photos: light grid backing + two dark pads.
  var inside = canvasTex(T, renderer, 1024, 530, function (g, w, h) {
    g.fillStyle = '#e6e7e8'; g.fillRect(0, 0, w, h);
    g.strokeStyle = '#d0d3d6'; g.lineWidth = 2;
    for (var x = 0; x <= w; x += 52) { g.beginPath(); g.moveTo(x, 0); g.lineTo(x, h); g.stroke(); }
    for (var y = 0; y <= h; y += 52) { g.beginPath(); g.moveTo(0, y); g.lineTo(w, y); g.stroke(); }
    [0.3, 0.64].forEach(function (fx) {
      var pw = w * 0.085, ph = h * 0.66, px = w * fx - pw / 2, py = (h - ph) / 2;
      var gr = g.createLinearGradient(px, 0, px + pw, 0);
      gr.addColorStop(0, '#1f2124'); gr.addColorStop(0.5, '#34373b'); gr.addColorStop(1, '#1f2124');
      var rr = pw / 2;
      g.fillStyle = gr; g.beginPath();
      g.moveTo(px, py + rr); g.arc(px + rr, py + rr, rr, Math.PI, 0, false);
      g.lineTo(px + pw, py + ph - rr); g.arc(px + rr, py + ph - rr, rr, 0, Math.PI, false);
      g.closePath(); g.fill();
    });
    var v = g.createRadialGradient(w / 2, h / 2, h * 0.3, w / 2, h / 2, w * 0.62);
    v.addColorStop(0, 'rgba(0,0,0,0)'); v.addColorStop(1, 'rgba(0,0,0,0.22)');
    g.fillStyle = v; g.fillRect(0, 0, w, h);
  });
  var back = new T.Mesh(new T.PlaneGeometry(win.w + 0.02, win.h + 0.02), new T.MeshStandardMaterial({ map: inside, roughness: 0.85 }));
  back.position.set(win.cx, win.cy, front - 0.17);

  // Clear anti-fog touch window: mostly transparent, but catches studio reflections as it turns.
  var glass = new T.Mesh(new T.PlaneGeometry(win.w - 0.02, win.h - 0.02), new T.MeshPhysicalMaterial({
    color: 0xffffff, roughness: 0.05, metalness: 0, transparent: true, opacity: 0.26, clearcoat: 1, clearcoatRoughness: 0.03, envMapIntensity: 2.6
  }));
  glass.position.set(win.cx, win.cy, front - 0.02);

  // Round speaker grille with diagonal slots (as in the photos) and a raised rim.
  var grilleTex = canvasTex(T, renderer, 512, 512, function (g, w, h) {
    g.fillStyle = '#9aa0a6'; g.beginPath(); g.arc(w / 2, h / 2, w / 2, 0, Math.PI * 2); g.fill();
    g.save(); g.beginPath(); g.arc(w / 2, h / 2, w / 2 - 10, 0, Math.PI * 2); g.clip();
    g.strokeStyle = '#3a3e43'; g.lineWidth = 7;
    for (var i = -w; i < w * 2; i += 19) { g.beginPath(); g.moveTo(i, 0); g.lineTo(i - h, h); g.stroke(); }
    var sh = g.createRadialGradient(w * 0.38, h * 0.32, 0, w / 2, h / 2, w / 2);
    sh.addColorStop(0, 'rgba(255,255,255,0.18)'); sh.addColorStop(1, 'rgba(0,0,0,0.25)');
    g.fillStyle = sh; g.fillRect(0, 0, w, h);
    g.restore();
  });
  var gR = 0.2, gY = 0.2;
  var grille = new T.Mesh(new T.CircleGeometry(gR, 64), new T.MeshStandardMaterial({ map: grilleTex, roughness: 0.55, metalness: 0.1 }));
  grille.position.set(panelX, gY, front + 0.002);
  var rim = new T.Mesh(new T.TorusGeometry(gR + 0.012, 0.016, 16, 96), accentMat);
  rim.position.set(panelX, gY, front + 0.004);

  // Control button and status indicator.
  var btn = new T.Mesh(new T.CylinderGeometry(0.075, 0.08, 0.03, 48), accentMat);
  btn.rotation.x = Math.PI / 2; btn.position.set(panelX, -0.17, front + 0.012);
  var led = new T.Mesh(new T.CapsuleGeometry(0.022, 0.07, 6, 16), new T.MeshStandardMaterial({ color: 0x3a3d41, roughness: 0.5 }));
  led.rotation.z = Math.PI / 2; led.position.set(panelX, -0.38, front + 0.006);

  var group = new T.Group();
  [body, back, glass, grille, rim, btn, led].forEach(function (m) { group.add(m); });
  glass.renderOrder = 2;

  var palettes = {
    white: { body: 0xefefec, accent: 0xdfe2e4, rough: 0.34 },
    black: { body: 0x1c1e21, accent: 0x2b2e32, rough: 0.42 }
  };
  return {
    group: group, W: W, H: H, D: DEPTH + BEV * 2,
    setVariant: function (name) {
      var p = palettes[name] || palettes.white;
      bodyMat.color.setHex(p.body); bodyMat.roughness = p.rough; accentMat.color.setHex(p.accent);
    }
  };
}

function buildShadow(T, renderer) {
  var tex = canvasTex(T, renderer, 256, 128, function (g, w, h) {
    var gr = g.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, w / 2);
    gr.addColorStop(0, 'rgba(20,22,26,0.55)'); gr.addColorStop(0.45, 'rgba(20,22,26,0.22)'); gr.addColorStop(1, 'rgba(20,22,26,0)');
    g.save(); g.scale(1, h / w); g.fillStyle = gr; g.fillRect(0, 0, w, w); g.restore();
  });
  var m = new T.Mesh(new T.PlaneGeometry(1, 1), new T.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, toneMapped: false }));
  m.rotation.x = -Math.PI / 2;
  return m;
}

/* ---------- scroll keyframes ----------
 * ry/rx: rotation (radians); x/y: offset as a fraction of the visible half-width/height;
 * s: scale; z: camera distance multiplier. Plateaus give each caption time to be read.
 */
var KF_DESKTOP = [
  { p: 0.00, ry: 0.00, rx: 0.00, x: 0.00, y: 0.17, s: 0.92, z: 1.00 },
  { p: 0.12, ry: 0.00, rx: 0.00, x: 0.00, y: 0.17, s: 0.92, z: 1.00 },
  { p: 0.32, ry: 0.56, rx: 0.07, x: 0.38, y: 0.02, s: 0.92, z: 0.98 },
  { p: 0.44, ry: 0.62, rx: 0.07, x: 0.38, y: 0.02, s: 0.92, z: 0.98 },
  { p: 0.64, ry: -0.98, rx: -0.07, x: -0.38, y: 0.02, s: 0.94, z: 0.97 },
  { p: 0.76, ry: -1.04, rx: -0.07, x: -0.38, y: 0.02, s: 0.94, z: 0.97 },
  { p: 0.92, ry: -0.42, rx: 0.13, x: 0.36, y: 0.04, s: 0.98, z: 0.93 },
  { p: 1.00, ry: -0.42, rx: 0.13, x: 0.36, y: 0.04, s: 0.98, z: 0.93 }
];
var KF_MOBILE = KF_DESKTOP.map(function (k) {
  return { p: k.p, ry: k.ry * 0.9, rx: k.rx, x: 0, y: 0.24, s: Math.min(k.s, 0.9), z: k.z };
});
var CAPTIONS = [[-1, 0.2], [0.25, 0.5], [0.57, 0.83], [0.87, 2]];

function sample(kf, p) {
  if (p <= kf[0].p) return kf[0];
  for (var i = 0; i < kf.length - 1; i++) {
    var a = kf[i], b = kf[i + 1];
    if (p <= b.p) {
      var t = easeIO((p - a.p) / (b.p - a.p || 1)), o = {};
      for (var k in a) o[k] = a[k] + (b[k] - a[k]) * t;
      return o;
    }
  }
  return kf[kf.length - 1];
}

async function start(root, reduce) {
  var T = await import(root.getAttribute('data-three'));
  var stage = root.querySelector('.sh-3d__stage');
  var canvas = root.querySelector('.sh-3d__canvas');
  var caps = [].slice.call(root.querySelectorAll('[data-sh-3d-cap]'));
  var bar = root.querySelector('.sh-3d__progress span');
  var mq = window.matchMedia('(max-width: 989px)');
  var lowPower = (navigator.hardwareConcurrency || 8) <= 4;

  var renderer = new T.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true, powerPreference: 'high-performance' });
  renderer.outputColorSpace = T.SRGBColorSpace;
  renderer.toneMapping = T.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.05;
  renderer.setClearColor(0x000000, 0);

  var scene = new T.Scene();
  var pmrem = new T.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(studio(T), 0.04).texture;
  pmrem.dispose();

  var key = new T.DirectionalLight(0xffffff, 1.0); key.position.set(-3, 4, 5); scene.add(key);
  scene.add(new T.HemisphereLight(0xffffff, 0xd9d4cc, 0.3));

  var model = buildModel(T, renderer);
  var pivot = new T.Group(); pivot.add(model.group); scene.add(pivot);
  var shadow = buildShadow(T, renderer); scene.add(shadow);
  var camera = new T.PerspectiveCamera(28, 1, 0.1, 100);
  var tanHalf = Math.tan(T.MathUtils.degToRad(camera.fov / 2));

  var baseZ = 8;
  function resize() {
    var w = stage.clientWidth, h = stage.clientHeight;
    if (!w || !h) return;
    var mobile = mq.matches;
    var dpr = Math.min(window.devicePixelRatio || 1, mobile ? (lowPower ? 1.25 : 1.6) : 2);
    renderer.setPixelRatio(dpr);
    renderer.setSize(w, h, false);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    var f = mobile ? 0.92 : 0.5, g = mobile ? 0.4 : 0.56; // share of width / height the product may use
    baseZ = Math.max(model.W / (2 * f * tanHalf * camera.aspect), model.H / (2 * g * tanHalf));
    render();
  }

  function readProgress() {
    if (reduce) return 0.96;
    var r = root.getBoundingClientRect();
    var span = r.height - window.innerHeight;
    return span > 0 ? clamp(-r.top / span, 0, 1) : 0;
  }

  var cur = readProgress();
  function apply(p) {
    var k = sample(mq.matches ? KF_MOBILE : KF_DESKTOP, p);
    var camZ = baseZ * k.z;
    var hw = tanHalf * camZ * camera.aspect, hh = tanHalf * camZ;
    camera.position.set(0, 0.04 * camZ, camZ);
    camera.lookAt(0, 0, 0);
    pivot.position.set(k.x * hw, k.y * hh, 0);
    pivot.rotation.set(k.rx, k.ry, 0);
    pivot.scale.setScalar(k.s);
    // Soft contact shadow follows the product and its turned width.
    var turned = (Math.abs(Math.cos(k.ry)) * model.W + Math.abs(Math.sin(k.ry)) * model.D) * k.s;
    shadow.position.set(pivot.position.x, pivot.position.y - (model.H / 2) * k.s - 0.28, 0);
    shadow.scale.set(turned * 1.25, model.D * k.s * 3.2 + 0.5, 1);
    if (!reduce) {
      caps.forEach(function (c, i) {
        var r = CAPTIONS[i] || [2, 2], e = 0.05;
        var o = clamp(Math.min((p - r[0]) / e, (r[1] - p) / e), 0, 1);
        c.style.opacity = o.toFixed(3);
        c.style.transform = 'translate3d(0,' + ((1 - o) * 22).toFixed(1) + 'px,0)';
        c.style.visibility = o < 0.01 ? 'hidden' : 'visible';
      });
      if (bar) bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
    }
  }
  function render() { apply(cur); renderer.render(scene, camera); }

  // Render loop runs only while the section is on screen and the eased progress is still moving.
  var raf = 0, last = 0, visible = false;
  function loop(t) {
    raf = 0;
    var dt = last ? Math.min(0.05, (t - last) / 1000) : 1 / 60;
    last = t;
    var target = readProgress();
    cur += (target - cur) * (1 - Math.exp(-dt * 7));
    if (Math.abs(target - cur) < 0.0002) cur = target;
    render();
    if (cur !== target && visible) raf = requestAnimationFrame(loop);
    else last = 0;
  }
  function kick() { if (!raf && visible && !reduce) raf = requestAnimationFrame(loop); }

  new IntersectionObserver(function (es) {
    visible = es[es.length - 1].isIntersecting;
    if (visible) kick();
  }).observe(root);
  window.addEventListener('scroll', kick, { passive: true });
  if ('ResizeObserver' in window) new ResizeObserver(resize).observe(stage);
  else window.addEventListener('resize', resize);
  if (mq.addEventListener) mq.addEventListener('change', resize);

  // Follow the colour chosen in the buy section (html[data-sh-variant]).
  function syncVariant() {
    model.setVariant(document.documentElement.getAttribute('data-sh-variant') === 'black' ? 'black' : 'white');
    render();
  }
  new MutationObserver(syncVariant).observe(document.documentElement, { attributes: true, attributeFilter: ['data-sh-variant'] });

  resize();
  syncVariant();
  root.classList.add('is-3d');
}
